// Per-sleeve performance. Rolls the server's by_algo aggregates up by
// algo_preset (a preset can span multiple algo_ids). Optional "vs backtest"
// column fans out /api/journal/compare/{algo_id} to show how the deployed
// ghost/live cohort compares to the backtest cohort — "is it holding up vs
// the gauntlet". Gated behind a checkbox so the page is cheap by default.

import { useMemo, useState } from "react";
import { useQueries } from "@tanstack/react-query";
import { journal as journalApi } from "../api/endpoints";
import type { JournalAggregates, JournalAlgoCompare, JournalCohortStats } from "../api/types";
import { usd, pct } from "../lib/format";

interface Props {
  aggregates: JournalAggregates | undefined;
}

interface SleeveRow {
  preset: string;
  repId: number; // representative algo_id (most trades) for the compare call
  count: number;
  pnl: number;
  wins: number;
  losses: number;
  nSim: number;
  pnlSim: number;
  nLive: number;
  pnlLive: number;
}

function num(n: number | null | undefined): string {
  return n === null || n === undefined ? "—" : n.toFixed(2);
}

function cohortTitle(g: JournalCohortStats | undefined, bt: JournalCohortStats | undefined): string {
  const fmt = (c: JournalCohortStats | undefined, label: string) =>
    c
      ? `${label}: n=${c.count} avg ${usd(c.avg_pnl_per_trade, { sign: true })} ` +
        `win ${c.win_rate != null ? (c.win_rate * 100).toFixed(0) + "%" : "—"} ` +
        `PF ${num(c.profit_factor)}`
      : `${label}: —`;
  return `${fmt(g, "deployed")}\n${fmt(bt, "backtest")}`;
}

export function SleevePerfTable({ aggregates }: Props) {
  const [showBt, setShowBt] = useState(false);

  const rows = useMemo<SleeveRow[]>(() => {
    if (!aggregates) return [];
    const agg = new Map<string, SleeveRow>();
    const rep = new Map<string, { id: number; count: number }>();
    for (const a of aggregates.by_algo) {
      const r = agg.get(a.algo_preset) ?? { preset: a.algo_preset, repId: a.algo_id, count: 0, pnl: 0, wins: 0, losses: 0, nSim: 0, pnlSim: 0, nLive: 0, pnlLive: 0 };
      r.count += a.count;
      r.pnl += a.pnl_net;
      r.wins += a.wins;
      r.losses += a.losses;
      r.nSim += a.n_sim;
      r.pnlSim += a.pnl_sim;
      r.nLive += a.n_live;
      r.pnlLive += a.pnl_live;
      agg.set(a.algo_preset, r);
      const cur = rep.get(a.algo_preset);
      if (!cur || a.count > cur.count) rep.set(a.algo_preset, { id: a.algo_id, count: a.count });
    }
    const out = [...agg.values()].map((r) => ({ ...r, repId: rep.get(r.preset)?.id ?? r.repId }));
    out.sort((x, y) => y.pnl - x.pnl);
    return out;
  }, [aggregates]);

  const compareQs = useQueries({
    queries: showBt
      ? rows.map((r) => ({
          queryKey: ["journal", "compare", r.repId],
          queryFn: () => journalApi.compare(r.repId),
          staleTime: 60_000,
        }))
      : [],
  });

  const compareByPreset = new Map<string, JournalAlgoCompare>();
  if (showBt) {
    rows.forEach((r, i) => {
      const d = compareQs[i]?.data;
      if (d) compareByPreset.set(r.preset, d);
    });
  }

  if (!aggregates || rows.length === 0) {
    return (
      <div className="card">
        <h2>Per-sleeve</h2>
        <div className="muted small">no sleeve activity in this window</div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="row">
        <h2 style={{ margin: 0 }}>Per-sleeve <span className="muted small">— net by preset</span></h2>
        <div className="spacer" />
        <label className="row small" style={{ gap: 6, width: "auto" }}>
          <input
            type="checkbox"
            checked={showBt}
            onChange={(e) => setShowBt(e.target.checked)}
            style={{ width: "auto" }}
          />
          <span className="muted">vs backtest</span>
        </label>
      </div>
      <table>
        <thead>
          <tr>
            <th>Sleeve</th>
            <th className="num">Trades</th>
            <th className="num">Net P&amp;L</th>
            <th className="num">Win %</th>
            <th className="num" title="Sim vs live split of the trades/net above — matches how Edge Verification slices cohorts">
              sim / live
            </th>
            {showBt && <th className="num">Δ $/trade</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const decided = r.wins + r.losses;
            const wr = decided > 0 ? (100 * r.wins) / decided : 0;
            const pnlCls = r.pnl > 0 ? "good" : r.pnl < 0 ? "bad" : "";
            // Source split: "6s" = 6 sim trades, "2L" = 2 live. Net shown
            // per source when both cohorts exist so the row can't be
            // misread against an all-live Edge Verification row.
            const usd0 = (x: number) => (x < 0 ? "-$" : "+$") + Math.round(Math.abs(x)).toLocaleString("en-US");
            const srcSplit =
              r.nSim > 0 && r.nLive > 0
                ? `${r.nSim}s ${usd0(r.pnlSim)} · ${r.nLive}L ${usd0(r.pnlLive)}`
                : r.nLive > 0
                  ? `${r.nLive}L`
                  : r.nSim > 0
                    ? `${r.nSim}s`
                    : "—";
            const cmp = compareByPreset.get(r.preset);
            const deployed =
              cmp?.cohorts.find((c) => c.source === "sim") ??
              cmp?.cohorts.find((c) => c.source === "live");
            const bt = cmp?.cohorts.find((c) => c.source === "backtest");
            const delta = deployed && bt ? deployed.avg_pnl_per_trade - bt.avg_pnl_per_trade : null;
            const dCls = delta == null ? "" : delta > 0 ? "good" : delta < 0 ? "bad" : "";
            const pending = showBt && !cmp;
            return (
              <tr key={r.preset}>
                <td><span className="mono">{r.preset}</span></td>
                <td className="num">{r.count}</td>
                <td className={"num " + pnlCls}>{usd(r.pnl, { sign: true })}</td>
                <td className="num muted">{pct(wr)}</td>
                <td className="num muted small">{srcSplit}</td>
                {showBt && (
                  <td className={"num " + dCls} title={cmp ? cohortTitle(deployed, bt) : ""}>
                    {pending ? <span className="muted">…</span> : delta == null ? "—" : usd(delta, { sign: true })}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      {showBt && (
        <div className="muted small" style={{ marginTop: 6 }}>
          Δ = deployed (sim/live) avg&nbsp;$/trade − backtest avg&nbsp;$/trade for each sleeve's
          busiest algo. Hover a cell for n / win% / PF on both cohorts.
        </div>
      )}
    </div>
  );
}
